import {
    Box,
    Container,
    Typography,
    Divider,
    IconButton,
    useMediaQuery,
    Card,
} from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, DesignServices, Launch, Code, Lightbulb } from '@mui/icons-material';
import { useThemeContext } from '../context/ThemeContext';
import Footer from '../components/Footer';
import { useRef, useEffect, useState } from 'react';

export default function About() {
    const { theme } = useThemeContext();
    const isMobile = useMediaQuery('(max-width: 768px)');

    // images section

    const heroImages = [
        '/about1.jpg',
        '/about4.png',
        '/about3.png',
    ];

    const [currentSlide, setCurrentSlide] = useState(0);
    const [paused, setPaused] = useState(false);
    const timerRef = useRef();
    const heroRef = useRef();
    const touchStartXRef = useRef(null);
    const touchEndXRef = useRef(null);

    useEffect(() => {
        if (!paused) {
            timerRef.current = setInterval(() => {
                setCurrentSlide((prev) => (prev + 1) % heroImages.length);
            }, 5000);
        }
        return () => clearInterval(timerRef.current);
    }, [paused]);

    const handleNext = () => setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    const handlePrev = () => setCurrentSlide((prev) => (prev === 0 ? heroImages.length - 1 : prev - 1));

    // Swipe handling
    useEffect(() => {
        const container = heroRef.current;
        if (!container) return;

        const handleTouchStart = (e) => {
            touchStartXRef.current = e.changedTouches[0].screenX;
        };
        const handleTouchEnd = (e) => {
            touchEndXRef.current = e.changedTouches[0].screenX;
            const distance = touchStartXRef.current - touchEndXRef.current;
            if (distance > 50) handleNext();
            else if (distance < -50) handlePrev();
        };

        container.addEventListener('touchstart', handleTouchStart);
        container.addEventListener('touchend', handleTouchEnd);
        return () => {
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchend', handleTouchEnd);
        };
    }, []);

    // images section end


    const scrollRef = useRef();
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    const journeyData = [
        { year: '2021', text: 'Founded House of Technology' },
        { year: '2022', text: 'Launched first SaaS product' },
        { year: '2023', text: 'Scaled with AI tools' },
        { year: '2024', text: 'Recognized globally' },
        { year: '2025', text: 'Ventured into Robotics' },
    ];

    const howWeWork = [
        {
            title: 'Ideate',
            description: 'Understand business needs and define goals.',
            icon: <Lightbulb fontSize="large" sx={{ color: '#2196f3' }} />
        },
        {
            title: 'Design',
            description: 'Craft intuitive and user-friendly experiences.',
            icon: <DesignServices fontSize="large" sx={{ color: '#9c27b0' }} />
        },
        {
            title: 'Develop',
            description: 'Build scalable, modern digital products.',
            icon: <Code fontSize="large" sx={{ color: '#4caf50' }} />
        },
        {
            title: 'Launch',
            description: 'Deploy and monitor performance post-launch.',
            icon: <Launch fontSize="large" sx={{ color: '#f44336' }} />
        },
    ];

    const updateScrollStatus = () => {
        const el = scrollRef.current;
        if (el) {
            const scrollLeft = el.scrollLeft;
            const maxScrollLeft = el.scrollWidth - el.clientWidth;

            setCanScrollLeft(scrollLeft > 5);
            setCanScrollRight(scrollLeft < maxScrollLeft - 5);
        }
    };

    const scroll = (dir) => {
        const amount = 300;
        scrollRef.current?.scrollBy({
            left: dir === 'left' ? -amount : amount,
            behavior: 'smooth',
        });
    };

    useEffect(() => {
        const el = scrollRef.current;
        updateScrollStatus();
        el?.addEventListener('scroll', updateScrollStatus);
        return () => el?.removeEventListener('scroll', updateScrollStatus);
    }, []);

    return (
        <Box sx={{ backgroundColor: theme.background, color: '#fff' }}>
            {/* Fullscreen Hero Section */}
            <Box
                ref={heroRef}
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100vh',
                    overflow: 'hidden',
                }}
            >
                {/* Background Images */}
                {heroImages.map((img, idx) => (
                    <Box
                        key={idx}
                        sx={{
                            backgroundImage: `url(${img})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            opacity: idx === currentSlide ? 1 : 0,
                            transition: 'opacity 1s ease-in-out',
                            zIndex: 0,
                        }}
                    />
                ))}

                {/* Overlay */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        zIndex: 1,
                    }}
                />

                {/* Arrows */}
                <IconButton
                    onClick={handlePrev}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: 10,
                        transform: 'translateY(-50%)',
                        zIndex: 3,
                        color: '#fff',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                    }}
                >
                    <ArrowBackIos />
                </IconButton>

                <IconButton
                    onClick={handleNext}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        right: 10,
                        transform: 'translateY(-50%)',
                        zIndex: 3,
                        color: '#fff',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                    }}
                >
                    <ArrowForwardIos />
                </IconButton>

                {/* Hero Content */}
                <Box
                    sx={{
                        zIndex: 2,
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        px: 3,
                    }}
                >
                    <Typography
                        variant="h2"
                        fontWeight={700}
                        sx={{
                            color: '#fff',
                            fontSize: { xs: '3.4rem', sm: '4.2rem', md: '5rem' },
                            textShadow: '2px 2px 8px rgba(0,0,0,0.7)',
                        }}
                    >
                        About Us
                    </Typography>
                </Box>

                {/* Progress Bars */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 20,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 3,
                        display: 'flex',
                        gap: 1.5,
                    }}
                >
                    {heroImages.map((_, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                width: 40,
                                height: 4,
                                borderRadius: 2,
                                overflow: 'hidden',
                                backgroundColor: '#ffffff33',
                                position: 'relative',
                            }}
                        >
                            {idx === currentSlide && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        height: '100%',
                                        width: '100%',
                                        backgroundColor: '#ffffff',
                                        animation: paused ? 'none' : 'progressBar 5s linear',
                                    }}
                                />
                            )}
                        </Box>
                    ))}
                </Box>

                {/* Animation Keyframes */}
                <style>
                    {`
            @keyframes progressBar {
                from { width: 0%; }
                to { width: 100%; }
            }
        `}
                </style>
            </Box>


            {/* About Content */}
            <Container sx={{ py: 5, px: { xs: 2, sm: 6, md: 10 } }}>
                <Typography variant="h5" fontWeight={500} fontSize="30px" gutterBottom sx={{ color: theme.text }}>
                    Who We Are
                </Typography>
                <Typography variant="body1" fontSize="20px" sx={{ color: theme.text }}>
                    House of Technology (HOT) is a forward-thinking IT consultancy and solutions provider committed to delivering future-ready digital solutions for businesses of all sizes and sectors. As a trusted technology partner, we specialize in offering end-to-end services that empower organizations to thrive in today’s fast-evolving digital landscape.
                    From custom-designed websites and robust CRM systems to feature-rich mobile applications, ERP systems, and UI/UX designs, we develop solutions that are tailored to meet your unique business challenges. Our offerings are not only technically sound but are also built with a deep understanding of modern user behavior and business goals.
                    At HOT, we combine innovation, strategy, and technology to transform your digital presence. Whether you are a retail business looking to expand your online reach, a hospitality chain seeking centralized management systems, or an enterprise in need of automation—we have the tools, talent, and experience to make it happen.
                    What sets us apart is our dedicated team of highly qualified professionals, each bringing deep industry knowledge and a passion for creating real impact. Our solutions are scalable, secure, and built with future growth in mind, ensuring your business is not only competitive today but ready for tomorrow.
                    We believe in building strong, collaborative relationships with our clients. Our approach is consultative and customer-centric, meaning we listen, understand, and deliver results that align with your vision. HOT isn’t just another IT vendor—we’re your long-term partner in digital transformation.
                    If you're ready to take your business to the next level with innovative and intelligent tech solutions, House of Technology is here to lead the way.

                </Typography>

                <Divider sx={{ my: 4, backgroundColor: 'gray' }} />

                <Typography variant="h5" fontWeight={500} fontSize="30px" sx={{ color: theme.text }} gutterBottom>
                    Our Mission
                </Typography>
                <Typography variant="body1" fontSize="20px" sx={{ color: theme.text }}>
                    At HOT (House of Technologies), our mission is to drive large-scale digital transformation by providing cutting-edge technology solutions tailored to evolving business needs. We recognize that many successful business owners are still managing operations with outdated systems and manual processes. Our objective is to bridge this gap by offering smart, scalable, and efficient digital solutions that enhance user experiences and operational productivity.
                    HOT is committed to supporting the vision of Digital India by helping businesses transition from traditional practices to modern technology platforms. Through detailed market research and in-depth surveys, we’ve identified that mismanagement and over-reliance on manpower remain significant pain points for many businesses. At HOT, we believe the solution isn’t always in hiring more people—it’s about working smarter with the right tools.
                    Our expert team is passionate about creating real impact. We don’t just work with clients—we build lasting partnerships. We aim to be more than a service provider; we aspire to be your technology partner in growth. Our focus is on delivering tailored IT solutions—from web and application development to CRM and ERP systems—that meet your specific business goals.
                    Join us in embracing the digital revolution. Let HOT help you modernize, streamline, and grow your business while taking pride in being a true promoter of Digital India.
                    Ready to elevate your business? Book your consultation with HOT today.
                </Typography>
            </Container>

            {/* Unified Modern "Our Process" Section */}
            <Box sx={{ py: 6, backgroundColor: theme.headerFooter }}>
                <Typography variant="h4" textAlign="center" gutterBottom                     
                sx={{ color: theme.text, maxWidth: 800, mx: 'auto', mb: 4 }}
>
                    How We Work
                </Typography>
                <Typography
                    variant="body1"
                    textAlign="center"
                    sx={{ color: 'rgba(255,255,255,0.7)', maxWidth: 800, mx: 'auto', mb: 4 }}
                >
                    At House of Technology, we follow a structured, transparent, and collaborative approach to project execution—designed to deliver high-quality results efficiently and on time. Our development process is grounded in Agile methodology, allowing us to release features in well-defined sprints for faster, flexible, and more manageable delivery.

                </Typography>

                {/* All Steps (Unified Flow) */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: 4,
                        px: 2,
                    }}
                >
                    {[
                        {
                            title: 'Ideation',
                            description: 'We start by deeply understanding your goals and audience.',
                            icon: <Lightbulb sx={{ fontSize: 40, color: '#2196f3' }} />,
                        },
                        {
                            title: 'UX/UI Design',
                            description: 'Our design team crafts delightful and user-first experiences.',
                            icon: <DesignServices sx={{ fontSize: 40, color: '#9c27b0' }} />,
                        },
                        {
                            title: 'Development',
                            description: 'Robust backend + responsive frontend using agile sprints.',
                            icon: <Code sx={{ fontSize: 40, color: '#4caf50' }} />,
                        },
                        {
                            title: 'Testing',
                            description: 'QA across devices, edge cases, and performance scenarios.',
                            icon: '🧪',
                        },
                        {
                            title: 'Client Feedback',
                            description: 'Incorporate feedback from stakeholders before go-live.',
                            icon: '🗣️',
                        },
                        {
                            title: 'Launch',
                            description: 'Smooth deployment with monitoring, analytics, and support.',
                            icon: <Launch sx={{ fontSize: 40, color: '#f44336' }} />,
                        },
                    ].map((step, idx) => (
                        <Card
                            key={idx}
                            sx={{
                                width: 250,
                                backgroundColor: '#1e1e1e',
                                color: '#fff',
                                textAlign: 'center',
                                p: 2,
                                borderRadius: 2,
                                transition: 'all 0.3s',
                                '&:hover': {
                                    transform: 'translateY(-5px) scale(1.05)',
                                    boxShadow: `0 4px 20px rgba(255,255,255,0.2)`,
                                },
                            }}
                            elevation={3}
                        >
                            <Box sx={{ mb: 2 }}>
                                {typeof step.icon === 'string' ? (
                                    <Typography fontSize="40px">{step.icon}</Typography>
                                ) : (
                                    step.icon
                                )}
                            </Box>
                            <Typography variant="h6">{step.title}</Typography>
                            <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.7)' }}>
                                {step.description}
                            </Typography>
                        </Card>
                    ))}
                </Box>
            </Box>


            {/* Journey Timeline */}
            <Box sx={{ position: 'relative', py: 8, px: 2, color: theme.text }}>
                <Typography variant="h4" textAlign="center" gutterBottom>
                    Our Journey
                </Typography>

                {/* Arrows */}
                <IconButton
                    onClick={() => scroll('left')}
                    disabled={!canScrollLeft}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: 10,
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        backgroundColor: '#1e1e1e',
                        opacity: canScrollLeft ? 1 : 0.4,
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <ArrowBackIos
                        sx={{
                            color: '#fff',
                            fontSize: 18,
                            ml: '2px', // adjust arrow alignment inside circle
                        }}
                    />
                </IconButton>

                <IconButton
                    onClick={() => scroll('right')}
                    disabled={!canScrollRight}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        right: 10,
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        backgroundColor: '#1e1e1e',
                        opacity: canScrollRight ? 1 : 0.4,
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <ArrowForwardIos
                        sx={{
                            color: '#fff',
                            fontSize: 18,
                            mr: '2px', // match left arrow offset visually
                        }}
                    />
                </IconButton>

                {/* Scrollable Timeline */}
                <Box
                    ref={scrollRef}
                    sx={{
                        overflowX: 'hidden',
                        px: 6,
                        mt: 6,
                        whiteSpace: 'nowrap',
                        scrollBehavior: 'smooth',
                    }}
                >
                    <Box sx={{ display: 'inline-flex', gap: 8, position: 'relative', pb: 6 }}>
                        {/* Horizontal Line */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '36px',
                                left: 0,
                                height: '4px',
                                width: '100%',
                                backgroundColor: '#777',
                                zIndex: 1,
                            }}
                        />

                        {/* Points */}
                        {journeyData.map((item, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    zIndex: 2,
                                    minWidth: 200,
                                    '&:hover .dot': {
                                        backgroundColor: '#64b5f6',
                                        boxShadow: '0 0 10px 4px rgba(100,181,246,0.5)',
                                        transform: 'scale(1.2)',
                                    },
                                    color: theme.text
                                }}
                            >
                                <Typography variant="body1" fontWeight={600} mb={1}>
                                    {item.year}
                                </Typography>
                                <Box
                                    className="dot"
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        backgroundColor: '#fff',
                                        border: '2px solid #ccc',
                                        transition: 'all 0.3s',
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mt: 1,
                                        color: 'rgba(255,255,255,0.7)',
                                        textAlign: 'center',
                                        maxWidth: 160,
                                        color: theme.text
                                    }}
                                >
                                    {item.text}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            <Footer />
        </Box>
    );
}
